import { Dispatch, SetStateAction, useCallback } from 'react'
import { hydrateImageNaturalSize, isHydratableImage } from '../../elements/hydrateImage'
import {
    insertListItem,
    insertTableCol,
    insertTableRow,
    removeListItem,
    removeTableCol,
    removeTableRow,
    resizeTableColAt,
    resizeTableRowAt,
} from '../../elements/tableListOps'
import { defaultEditorTheme } from '../../templateEditor.theme'
import { PlaceModeOptions, Rect, TemplateElement, ImageElement } from '../../templateEditor.types'
import { clampRectToCanvas } from '../../templateEditor.utils'
import { ratioForElement, shouldPreserveRatio } from './documentPointer'
import { useTemplateEditorHistory } from './useTemplateEditorHistory'

const theme = defaultEditorTheme

type HistoryApi = ReturnType<typeof useTemplateEditorHistory>

type Params = {
    canvasW: number
    canvasH: number
    setElements: Dispatch<SetStateAction<TemplateElement[]>>
    history: HistoryApi
}

export const useTemplateEditorElementOps = ({ canvasW, canvasH, setElements, history }: Params) => {
    const insertElement = useCallback(
        (element: TemplateElement) => {
            history.mutateElements((prev) => [...prev, element], [element.id])

            if (isHydratableImage(element)) {
                hydrateImageNaturalSize(element, (patch) => {
                    setElements((prev) =>
                        prev.map((item) =>
                            item.id === element.id && item.type === 'image' ? { ...item, ...patch } : item
                        )
                    )
                })
            }
        },
        [history, setElements]
    )

    const insertTableRowAt = useCallback(
        (elementId: string, index: number) => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'table' ? insertTableRow(element, index) : element
                )
            )
        },
        [history, setElements]
    )

    const insertTableColAt = useCallback(
        (elementId: string, index: number) => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'table' ? insertTableCol(element, index) : element
                )
            )
        },
        [history, setElements]
    )

    const resizeTableRow = useCallback(
        (elementId: string, borderIndex: number, deltaLogical: number) => {
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'table'
                        ? resizeTableRowAt(element, borderIndex, deltaLogical)
                        : element
                )
            )
        },
        [setElements]
    )

    const resizeTableCol = useCallback(
        (elementId: string, borderIndex: number, deltaLogical: number) => {
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'table'
                        ? resizeTableColAt(element, borderIndex, deltaLogical)
                        : element
                )
            )
        },
        [setElements]
    )

    const insertListItemAt = useCallback(
        (elementId: string, index: number) => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'list'
                        ? insertListItem(element, index, theme)
                        : element
                )
            )
        },
        [history, setElements]
    )

    const removeTableRowAt = useCallback(
        (elementId: string, index: number) => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'table' ? removeTableRow(element, index) : element
                )
            )
        },
        [history, setElements]
    )

    const removeTableColAt = useCallback(
        (elementId: string, index: number) => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'table' ? removeTableCol(element, index) : element
                )
            )
        },
        [history, setElements]
    )

    const removeListItemAt = useCallback(
        (elementId: string, index: number) => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'list'
                        ? removeListItem(element, index, theme)
                        : element
                )
            )
        },
        [history, setElements]
    )

    const setElementRotate = useCallback(
        (elementId: string, rotate: number) => {
            history.pushHistory()
            setElements((prev) => prev.map((element) => (element.id === elementId ? { ...element, rotate } : element)))
        },
        [history, setElements]
    )

    const updateElementRect = useCallback(
        (id: string, rect: Rect) => {
            const next = clampRectToCanvas(rect, canvasW, canvasH, theme.minElSize)
            setElements((prev) => prev.map((element) => (element.id === id ? { ...element, ...next } : element)))
        },
        [canvasH, canvasW, setElements]
    )

    const setElementRect = useCallback(
        (id: string, patch: Partial<Rect>) => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) => {
                    if (element.id !== id) {
                        return element
                    }

                    let next: Rect = {
                        x: element.x,
                        y: element.y,
                        w: element.w,
                        h: element.h,
                        ...patch,
                    }

                    const changingW = typeof patch.w === 'number'
                    const changingH = typeof patch.h === 'number'
                    // У картинок W/H независимы (кадр); пропорции — через fit/углы/objectFit.
                    if (element.type !== 'image' && shouldPreserveRatio(element) && changingW !== changingH) {
                        const ratio = ratioForElement(element)
                        if (changingW) {
                            next.h = Math.max(theme.minElSize, Math.round(next.w / ratio))
                        } else {
                            next.w = Math.max(theme.minElSize, Math.round(next.h * ratio))
                        }
                    }

                    const clamped = clampRectToCanvas(next, canvasW, canvasH, theme.minElSize)
                    return { ...element, ...clamped }
                })
            )
        },
        [canvasH, canvasW, history, setElements]
    )

    const patchElement = useCallback(
        (id: string, patch: Partial<TemplateElement>) => {
            setElements((prev) =>
                prev.map((element) => {
                    if (element.id !== id) {
                        return element
                    }
                    return { ...element, ...patch } as TemplateElement
                })
            )
        },
        [setElements]
    )

    const replaceImageContent = useCallback(
        (elementId: string, payload: PlaceModeOptions) => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'image'
                        ? {
                              ...element,
                              src: payload.src ?? element.src,
                              sourceId: payload.sourceId ?? element.sourceId,
                              thumbnail: payload.thumbnail ?? element.thumbnail,
                              name: payload.name || element.name,
                              naturalW: payload.naturalW ?? element.naturalW,
                              naturalH: payload.naturalH ?? element.naturalH,
                          }
                        : element
                )
            )
        },
        [history, setElements]
    )

    const resetImageToNaturalSize = useCallback(
        (elementId: string) => {
            history.mutateElements((prev) => {
                const target = prev.find((element) => element.id === elementId)
                if (!target || target.type !== 'image' || !target.src) {
                    return prev
                }

                const cleared: ImageElement = {
                    ...target,
                    naturalW: undefined,
                    naturalH: undefined,
                }

                hydrateImageNaturalSize(
                    cleared,
                    (patch) => {
                        setElements((next) =>
                            next.map((element) =>
                                element.id === elementId && element.type === 'image'
                                    ? { ...element, ...patch }
                                    : element
                            )
                        )
                    },
                    {
                        force: true,
                        keepCenter: true,
                        canvasW,
                        canvasH,
                        minElSize: theme.minElSize,
                    }
                )

                return prev.map((element) =>
                    element.id === elementId && element.type === 'image'
                        ? { ...element, naturalW: undefined, naturalH: undefined }
                        : element
                )
            })
        },
        [canvasH, canvasW, history, setElements]
    )

    /** Подгоняет кадр под natural-пропорции, сохраняя ширину или высоту. */
    const fitImageToAxis = useCallback(
        (elementId: string, axis: 'width' | 'height') => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) => {
                    if (element.id !== elementId || element.type !== 'image') {
                        return element
                    }

                    const naturalW = element.naturalW
                    const naturalH = element.naturalH
                    if (!naturalW || !naturalH) {
                        return element
                    }

                    const ratio = naturalW / naturalH
                    const cx = element.x + element.w / 2
                    const cy = element.y + element.h / 2
                    let w = element.w
                    let h = element.h

                    if (axis === 'width') {
                        h = Math.max(theme.minElSize, Math.round(w / ratio))
                    } else {
                        w = Math.max(theme.minElSize, Math.round(h * ratio))
                    }

                    const next = clampRectToCanvas(
                        { x: cx - w / 2, y: cy - h / 2, w, h },
                        canvasW,
                        canvasH,
                        theme.minElSize
                    )
                    return { ...element, ...next }
                })
            )
        },
        [canvasH, canvasW, history, setElements]
    )

    const setImageObjectFit = useCallback(
        (elementId: string, objectFit: ImageElement['objectFit']) => {
            history.pushHistory()
            setElements((prev) =>
                prev.map((element) =>
                    element.id === elementId && element.type === 'image' ? { ...element, objectFit } : element
                )
            )
        },
        [history, setElements]
    )

    return {
        insertElement,
        insertTableRowAt,
        insertTableColAt,
        removeTableRowAt,
        removeTableColAt,
        resizeTableRow,
        resizeTableCol,
        insertListItemAt,
        removeListItemAt,
        setElementRotate,
        updateElementRect,
        setElementRect,
        patchElement,
        replaceImageContent,
        resetImageToNaturalSize,
        fitImageToAxis,
        setImageObjectFit,
    }
}
